/**
 * 1stbuddha — slider.js
 *
 * <slider-component> adds controls to a native scroll-snap rail. The rail
 * itself is plain CSS (component-slider.css); this script only:
 *   - wires [data-slider-prev] / [data-slider-next] (scroll by one "page")
 *   - disables them at the ends and sets [data-scrollable] on the component
 *     (buttons and progress hide when everything fits, e.g. grid breakpoints)
 *   - renders the progress segments in [data-slider-progress]
 *
 * Markup
 *   <slider-component>
 *     <ul class="slider__track slider__track--rail-below-lg" data-slider-track>…</ul>
 *     <button data-slider-prev aria-label="Previous">…</button>
 *     <button data-slider-next aria-label="Next">…</button>
 *     <div class="slider__progress" data-slider-progress aria-hidden="true"></div>
 *   </slider-component>
 */
(() => {
  if (customElements.get('slider-component')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class SliderComponent extends HTMLElement {
    constructor() {
      super();
      this.update = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;

      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.progress = this.querySelector('[data-slider-progress]');

      this.prevButton?.addEventListener('click', () => this.go(-1));
      this.nextButton?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    get step() {
      const [first, second] = this.track.children;
      if (first && second) return second.offsetLeft - first.offsetLeft;
      return this.track.clientWidth;
    }

    go(direction) {
      const step = this.step;
      const perPage = Math.max(1, Math.floor((this.track.clientWidth + 1) / step));
      this.track.scrollBy({
        left: direction * step * perPage,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      const scrollable = max > 2;

      this.toggleAttribute('data-scrollable', scrollable);
      if (this.prevButton) this.prevButton.disabled = !scrollable || scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = !scrollable || scrollLeft >= max - 2;

      if (!this.progress) return;
      const pages = scrollable ? Math.max(2, Math.ceil(scrollWidth / clientWidth - 0.1)) : 0;
      if (this.progress.childElementCount !== pages) {
        this.progress.replaceChildren(
          ...Array.from({ length: pages }, () => {
            const segment = document.createElement('span');
            segment.className = 'slider__segment';
            return segment;
          })
        );
      }
      const active = scrollable ? Math.round((scrollLeft / max) * (pages - 1)) : -1;
      [...this.progress.children].forEach((segment, index) => {
        segment.classList.toggle('is-active', index === active);
      });
    }
  }

  customElements.define('slider-component', SliderComponent);
})();
